// Whether a user who never touched the newsletter toggle gets it. Off = explicit
// opt-in only; flip to true for soft opt-in of existing customers.
export const NEWSLETTER_DEFAULT_SUBSCRIBED = false

export const isNewsletterSubscribed = (optIn: boolean | undefined) =>
  optIn ?? NEWSLETTER_DEFAULT_SUBSCRIBED

export const NEWSLETTER_BATCH = 50
export const NEWSLETTER_BATCH_GAP_MS = 2_000
// Gmail clips messages over ~102KB; also keeps a 50-email batch well under write limits.
export const NEWSLETTER_MAX_HTML = 100_000
export const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

export const blogUrl = (slug: string) => `https://goaltree.ai/blog/${slug}`

const enc = new TextEncoder()
const b64url = (bytes: Uint8Array) =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
const fromB64url = (s: string) =>
  Uint8Array.from(atob(s.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0))

async function hmac(secret: string, data: string) {
  const key = await crypto.subtle.importKey(
    'raw',
    enc.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  )
  return new Uint8Array(await crypto.subtle.sign('HMAC', key, enc.encode(`newsletter:${data}`)))
}

// No expiry: unsubscribe links in old issues must keep working.
export async function signUnsubscribeToken(userId: string, secret: string) {
  const body = b64url(enc.encode(userId))
  return `${body}.${b64url(await hmac(secret, body))}`
}

export async function verifyUnsubscribeToken(token: string, secret: string) {
  const [body, sig] = token.split('.')
  if (!body || !sig) return null
  const expected = b64url(await hmac(secret, body))
  if (expected.length !== sig.length) return null
  let diff = 0
  for (let i = 0; i < sig.length; i++) diff |= expected.charCodeAt(i) ^ sig.charCodeAt(i)
  if (diff) return null
  try {
    return new TextDecoder().decode(fromB64url(body)) || null
  } catch {
    return null
  }
}

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

export function fillNewsletter(
  html: string,
  vars: { firstName: string; unsubscribeUrl: string; viewUrl: string },
) {
  return html
    .replace(/\{\{\s*first_name\s*\}\}/g, () => esc(vars.firstName))
    .replace(/\{\{\s*unsubscribe_url\s*\}\}/g, () => esc(vars.unsubscribeUrl))
    .replace(/\{\{\s*view_in_browser_url\s*\}\}/g, () => esc(vars.viewUrl))
}

export const firstNameOf = (name: string | undefined) => name?.trim().split(/\s+/)[0] || 'there'

// Hidden inbox preview line, right after <body>.
export function withPreheader(html: string, text: string) {
  if (!text.trim() || html.includes(text)) return html
  const div = `<div style="display:none;max-height:0;overflow:hidden;opacity:0">${esc(text)}</div>`
  return /<body[^>]*>/i.test(html) ? html.replace(/<body[^>]*>/i, (m) => m + div) : div + html
}
